<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100" id="map"></div>
    <div class="md-layout-item md-size-10 md-xsmall-size-100 md-medium-size-15">
      <md-button
        class="md-round md-just-icon md-icon"
        id="legend-icon"
        @click="legendVisible = !legendVisible"
      >
        <i class="fa fa-list"></i>
      </md-button>
    </div>
    <div class="map-overlay" id="legend" v-show="legendVisible">
      <h3 class="text-primary" style="text-align:center">
        <b>{{ $t("Map Legend") }}</b>
      </h3>
      <div class="legend-group">
        <div style="width: 100px; text-align: center">
          <svg height="50" width="50">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="#e53935"
              fill-opacity="0.2"
              stroke="#e53935"
              stroke-width="2"
            />
          </svg>
          <p>{{ $t("Advanced Operations") }}</p>
        </div>
        <div style="width: 100px; text-align: center">
          <svg height="50" width="50">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="#fdd835"
              fill-opacity="0.2"
              stroke="#fdd835"
              stroke-width="2"
            />
          </svg>
          <p>{{ $t("Caution Advised") }}</p>
        </div>
        <div style="width: 100px; text-align: center">
          <svg height="50" width="50">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="#039be5"
              fill-opacity="0.2"
              stroke="#039be5"
              stroke-width="2"
            />
          </svg>
          <p>{{ $t("> 700ft AGL Airspace") }}</p>
        </div>
      </div>
      <h3 class="text-primary" style="text-align:center">
        <b>{{ $t("Select Layers") }}</b>
      </h3>
      <div>
        <div class="layer-group-item">
          <md-checkbox v-model="showAirspace" @change="loadData">{{
            $t("airspace_below_700ft")
          }}</md-checkbox>
        </div>
        <div class="layer-group-item">
          <md-checkbox v-model="showAirspaceAboveSFC" @change="loadData">{{
            $t("airspace_above_700ft")
          }}</md-checkbox>
        </div>
        <div class="layer-group-item">
          <md-checkbox v-model="showAerodrome" @change="loadData">{{
            $t("Aerodromes Layer")
          }}</md-checkbox>
        </div>
      </div>
      <div style="text-align: center">
        <md-button
          class="md-raised md-primary mt-4"
          @click="legendVisible = false"
          >Close</md-button
        >
      </div>
    </div>
  </div>
</template>
<script>
import { mapFields } from "vuex-map-fields";
import { mapboxAccessToken, USER_TYPE } from "@/const.js";
import { EventBus } from "@/helpers";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { coordinatesGeocoder } from "@/helpers/utils";
import { mapPopupTpl } from "@/helpers/mapPopupTpl";

export default {
  data() {
    return {
      map: null,
      rpas: null,
      alert: false,
      latitude: 43.4723,
      longitude: -80.5449,
      legendVisible: false,
    };
  },
  computed: {
    ...mapFields("auth", ["usertype"]),
    ...mapFields("setting", [
      "airspaceGeoData",
      "airspaceAboveSFCGeoData",
      "aerodromeGeoData",
      "heliportGeoData",
      "showAirspace",
      "showAirspaceAboveSFC",
      "showAerodrome"
    ])
  },
  mounted() {
    this.loadData();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        this.longitude = pos.coords.longitude;
        this.latitude = pos.coords.latitude;
        this.loadData();
      });
    }
  },
  beforeDestroy() {
    EventBus.$off("setGeoData");
    this.map.remove();
  },
  methods: {
    loadData() {
      this.loadMap();

      this.$store.dispatch("setting/getGeoData", {
        lng: this.longitude,
        lat: this.latitude
      });

      EventBus.$on("setGeoData", () => {
        this.addSourceAndLayers();
      });
    },
    addSourceAndLayers() {
      if (this.map.getLayer("fills")) {
        this.map.removeLayer("fills");
      }
      if (this.map.getLayer("borders")) {
        this.map.removeLayer("borders");
      }
      if (this.map.getLayer("airspaces_symbol")) {
        this.map.removeLayer("airspaces_symbol");
      }
      if (this.map.getSource("airspaces")) {
        this.map.removeSource("airspaces");
      }
      if (this.map.getLayer("airspacesAboveSFC_fills")) {
        this.map.removeLayer("airspacesAboveSFC_fills");
      }
      if (this.map.getLayer("airspacesAboveSFC_borders")) {
        this.map.removeLayer("airspacesAboveSFC_borders");
      }
      if (this.map.getSource("airspacesAboveSFC")) {
        this.map.removeSource("airspacesAboveSFC");
      }
      if (this.map.getLayer("aerodromes_fills")) {
        this.map.removeLayer("aerodromes_fills");
      }
      if (this.map.getLayer("aerodromes_borders")) {
        this.map.removeLayer("aerodromes_borders");
      }
      if (this.map.getSource("aerodromes")) {
        this.map.removeSource("aerodromes");
      }

      if (this.map.getLayer("heliports_fills")) {
        this.map.removeLayer("heliports_fills");
      }
      if (this.map.getLayer("heliports_borders")) {
        this.map.removeLayer("heliports_borders");
      }
      if (this.map.getSource("heliports")) {
        this.map.removeSource("heliports");
      }

      if (this.showAirspaceAboveSFC) {
        this.map.addSource("airspacesAboveSFC", {
          type: "geojson",
          data: this.airspaceAboveSFCGeoData
        });
        this.map.addLayer({
          id: "airspacesAboveSFC_fills",
          type: "fill",
          source: "airspacesAboveSFC",
          layout: {},
          paint: {
            "fill-color": [
              "case",
              ["==", "SFC", ["get", "FLOOR"]],
              "#e53935",
              "#039be5"
            ],
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              0,
              0.2
            ]
          }
        });

        this.map.addLayer({
          id: "airspacesAboveSFC_borders",
          type: "line",
          source: "airspacesAboveSFC",
          layout: {},
          paint: {
            "line-color": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              "#FFFFFF",
              ["case", ["==", "SFC", ["get", "FLOOR"]], "#e53935", "#039be5"]
            ],
            "line-width": 2
          }
        });
      }

      if (this.showAirspace) {
        this.map.addSource("airspaces", {
          type: "geojson",
          data: this.airspaceGeoData
        });
        this.map.addLayer({
          id: "fills",
          type: "fill",
          source: "airspaces",
          layout: {},
          paint: {
            "fill-color": [
              "case",
              ["==", "SFC", ["get", "FLOOR"]],
              "#e53935",
              "#039be5"
            ],
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              0,
              0.2
            ]
          }
        });

        this.map.addLayer({
          id: "borders",
          type: "line",
          source: "airspaces",
          layout: {},
          paint: {
            "line-color": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              "#FFFFFF",
              ["case", ["==", "SFC", ["get", "FLOOR"]], "#e53935", "#039be5"]
            ],
            "line-width": 2
          }
        });

        this.map.addLayer({
          id: "airspaces_symbol",
          type: "symbol",
          source: "airspaces",
          layout: {
            'symbol-placement': 'line-center',
            'text-field': '{FLOOR} - {CEILING}',
            'text-anchor': 'bottom',
            "text-letter-spacing": 0.3,
            'text-size': 10,
          },
          paint: {
            'text-color': 'white',
          }
        });
      }

      if (this.showAerodrome) {
        this.map.addSource("aerodromes", {
          type: "geojson",
          data: this.aerodromeGeoData,
          generateId: true //This ensures that all features have unique IDs
        });
        this.map.addLayer({
          id: "aerodromes_fills",
          type: "fill",
          source: "aerodromes",
          layout: {},
          paint: {
            "fill-color": [
              "match",
              ["get", "OPR_TYPE"],
              "Cert",
              "#e53935",
              "Mil",
              "#e53935",
              "#fdd835"
            ],
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              0,
              0.2
            ]
          }
        });
        this.map.addLayer({
          id: "aerodromes_borders",
          type: "line",
          source: "aerodromes",
          layout: {},
          paint: {
            "line-color": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              "#FFFFFF",
              [
                "match",
                ["get", "OPR_TYPE"],
                "Cert",
                "#e53935",
                "Mil",
                "#e53935",
                "#fdd835"
              ]
            ],
            "line-width": 2
          }
        });

        if (this.usertype === USER_TYPE.MAAC) {
          this.map.addSource("heliports", {
            type: "geojson",
            data: this.heliportGeoData,
            generateId: true //This ensures that all features have unique IDs
          });
          this.map.addLayer({
            id: "heliports_fills",
            type: "fill",
            source: "heliports",
            layout: {},
            paint: {
              "fill-color": [
                "match",
                ["get", "OPR_TYPE"],
                "Cert",
                "#fdd835",
                "Mil",
                "#fdd835",
                "#fdd835"
              ],
              "fill-opacity": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                0,
                0.2
              ]
            }
          });
          this.map.addLayer({
            id: "heliports_borders",
            type: "line",
            source: "heliports",
            layout: {},
            paint: {
              "line-color": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                "#FFFFFF",
                [
                  "match",
                  ["get", "OPR_TYPE"],
                  "Cert",
                  "#fdd835",
                  "Mil",
                  "#fdd835",
                  "#fdd835"
                ]
              ],
              "line-width": 2
            }
          });
        }
      }
    },
    loadMap() {
      var self = this;

      mapboxgl.accessToken = mapboxAccessToken;
      this.map = new mapboxgl.Map({
        container: "map",
        style: "mapbox://styles/mapbox/satellite-streets-v12",
        center: [this.longitude, this.latitude],
        zoom: 10
      });

      var geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        localGeocoder: coordinatesGeocoder,
        countries: "ca",
        marker: false,
        mapboxgl: mapboxgl,
        language:
          window.localStorage.getItem("locale") === "fr" ? "fr-CA" : "en-US"
      });
      this.map.addControl(geocoder);

      geocoder.on("result", function(e) {
        self.longitude = e.result.center[0];
        self.latitude = e.result.center[1];

        self.$store.dispatch("setting/getGeoData", {
          lng: self.longitude,
          lat: self.latitude
        });
      });

      var hoveredFeatures = [];

      this.map.on("load", function() {
        //this.setFilter("fills", ["in", "CLASS", "C", "D", "E", "F"]);
        //this.setFilter("borders", ["in", "CLASS", "C", "D", "E", "F"]);

        var popup = new mapboxgl.Popup({
          closeButton: false,
          // closeOnClick: false
        });


        this.on("click", ["fills", "aerodromes_fills", "airspacesAboveSFC_fills", "heliports_fills"], function(e) {
          if (hoveredFeatures.length) {
              hoveredFeatures.forEach(item => {
                this.setFeatureState(
                  { source: item.source, id: item.id },
                  { hover: false }
                );
              })
              hoveredFeatures = [];

              popup.remove();
          }
          let HTML = ''
          const features = e.features.reduce((acc, currentValue) => {
            const isNameExists = acc.some(item => item.id === currentValue.id);
            if (!isNameExists) {
              acc.push(currentValue);
            }
            return acc;
          }, []);
          hoveredFeatures = features
          features.forEach((item, idx) => {
            HTML += mapPopupTpl(item, self.usertype)
            this.setFeatureState(
              { source: item.source, id: item.id },
              { hover: true }
            );
          })

          HTML = `<div style="max-height: 400px; overflow-y: auto;">${HTML}</div>`
          popup
            .setLngLat(e.lngLat)
            .setHTML(HTML)
            .addTo(this);
        });

        popup.on('close', () => {
          if (hoveredFeatures.length) {
            hoveredFeatures.forEach(item => {
              this.setFeatureState(
                { source: item.source, id: item.id },
                { hover: false }
              );
            })
            hoveredFeatures = [];

            popup.remove();
          }
        });

        this.on("dragend", () => {
          self.$store.dispatch("setting/getGeoData", {
            lng: self.longitude,
            lat: self.latitude
          });
        });

        this.on("drag", () => {
          var center = self.map.getCenter();
          self.longitude = center.lng;
          self.latitude = center.lat;
        });
      });
    }
  }
};
</script>
<style lang="css">
#map {
  width: 100%;
  height: 85vh;
  margin: 0px;
  padding: 0px;
}

.map-overlay {
  position: absolute;
  top: 0;
  right: 0;
  background: #fff;
  margin-right: 10px;
  font-family: Arial, sans-serif;
  overflow: auto;
  border-radius: 3px;
}

.legend-group {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  margin-top: 40px;
  margin-bottom: 40px;
}

.layer-group-item {
  margin-left: 20px;
}

#legend-icon {
  position: absolute;
  top: 0;
  right: 0;
  margin-top: 106px;
  margin-right: 260px;
}

#legend {
  padding: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  line-height: 18px;
  margin-top: 160px;
  width: 400px;
  height: 600px;
}
</style>
